import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categories } from "@/mocks/categories";
import {
  allGenres,
  allRegions,
  allYears,
  filterWorks,
  sortWorks,
  type SortMode,
} from "@/utils/catalog";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/feature/EmptyState";
import FilterBar, { type PendingFilters } from "@/pages/category/components/FilterBar";

const PAGE_SIZE = 9;

const sortTabs: { key: SortMode; label: string; icon: string }[] = [
  { key: "updated", label: "更新时间", icon: "ri-time-line" },
  { key: "popular", label: "热度", icon: "ri-fire-line" },
  { key: "rating", label: "评分", icon: "ri-star-line" },
];

const emptyFilters: PendingFilters = { genres: [], regions: [], years: [] };

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = categories.find((c) => c.id === categoryId);

  const [pending, setPending] = useState<PendingFilters>(emptyFilters);
  const [applied, setApplied] = useState<PendingFilters>(emptyFilters);
  const [sort, setSort] = useState<SortMode>("updated");
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: `${category?.name ?? "分类"} - 影视剧情介绍与推荐 - 妻子的朋友`,
    description: category?.description,
    keywords: `${category?.name ?? "影视"},影视剧情,剧情介绍,${category?.name ?? ""}推荐,妻子的朋友`,
  });

  const results = useMemo(() => {
    const list = filterWorks({
      type: category?.id,
      genres: applied.genres,
      regions: applied.regions,
      years: applied.years,
    });
    return sortWorks(list, sort);
  }, [category?.id, applied, sort]);

  const shown = results.slice(0, visible);
  const activeCount = applied.genres.length + applied.regions.length + applied.years.length;

  const toggle = (kind: keyof PendingFilters, value: string | number) => {
    setPending((prev) => {
      const arr = prev[kind] as (string | number)[];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [kind]: next } as PendingFilters;
    });
  };

  const apply = () => {
    setApplied(pending);
    setVisible(PAGE_SIZE);
  };

  const reset = () => {
    setPending(emptyFilters);
    setApplied(emptyFilters);
    setVisible(PAGE_SIZE);
  };

  if (!category) {
    return (
      <div className="mx-auto w-full max-w-[1360px] px-4 py-16 md:px-6">
        <EmptyState
          title="未找到该分类"
          description="你访问的分类不存在或已调整，返回首页看看其他精彩内容。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="flex h-11 items-center gap-2 rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1360px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "分类" }, { label: category.name }]} />

      <header className="mt-4 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5 md:flex-row md:items-center md:p-6">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
          <i className={`${category.icon} text-3xl`}></i>
        </span>
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {category.name}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
            {category.description}
          </p>
        </div>
      </header>

      <div className="mt-5 flex flex-col gap-4">
        <FilterBar
          genreOptions={allGenres}
          regionOptions={allRegions}
          yearOptions={allYears}
          pending={pending}
          activeCount={activeCount}
          resultCount={results.length}
          onToggle={toggle}
          onApply={apply}
          onReset={reset}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {sortTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setSort(t.key);
                  setVisible(PAGE_SIZE);
                }}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === t.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-700"
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={t.icon}></i>
                </span>
                {t.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-foreground-500">共 {results.length} 部作品</span>
        </div>
      </div>

      <div className="mt-6">
        {shown.length === 0 ? (
          <EmptyState
            title="没有匹配的影视作品"
            description="试试减少筛选条件，或重置后重新浏览该分类下的全部作品。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {shown.map((w, i) => (
                <WorkCard key={w.id} work={w} index={i} />
              ))}
            </div>
            {visible < results.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="flex h-11 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-add-line"></i>
                  </span>
                  加载更多
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}